/* eslint-env browser */
// My profile (wave 3, W2_Portal_Settings look): one card with the photo,
// one label above each field, errors under the field, one Save button; the
// code for a new phone number in a small dialog (full screen on a phone).
//
// Behaviour, exactly as before:
// - Save: names required; then a changed email goes through
//   checkUserExists + updateProfileWithEmail; a changed phone through
//   checkUserExists, then the code step (requestMobileOtp with
//   settings.customMobileOTP, else Firebase phone linking) and
//   updateAuthMobile / confirm before the profile is saved; otherwise
//   updateProfile({ firstName, lastName, email, mobile, verifyId, lang }),
//   the photo (updateWebProfileImage) and the language pack (loaded first
//   when it is not held yet). Email and phone can not both change at once.
// - Email and phone stay read-only here, as before (the old condition always
//   locked them); the code step is kept for when they are unlocked.
// - First and last name are locked for Oromia Bank accounts; the ID field
//   shows when ID approval is on or an ID is on file (not for admins).
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FirebaseContext, api } from 'common';
import { useTranslation } from 'react-i18next';
import moment from 'moment/min/moment-with-locales';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import { tokens as c } from '../theme';
import { processApiResponse } from '../utils/securityUtils';
import { showToast, InlineMessage } from '../components/feedback';
import { SectionCard, FieldLabel, FieldError, calmCaps, toNineDigits, toEthiopianE164 } from '../components/modern';

const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const MyProfile = () => {
  const { authRef, RecaptchaVerifier, unlink, linkWithPhoneNumber } = useContext(FirebaseContext);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phoneScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { updateProfile, updateWebProfileImage, updateProfileWithEmail, checkUserExists, requestMobileOtp, updateAuthMobile } = api;
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const languagedata = useSelector((state) => state.languagedata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const [langSelection, setLangSelection] = useState(0);
  const [multiLanguage, setMultiLanguage] = useState();
  const langRequestRef = useRef(0);
  const [langPending, setLangPending] = useState(false); // the chosen language is downloading
  const fileInputRef = useRef();
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  // "Profile updated" once per save (the save itself and the profile arriving
  // back used to show it twice).
  const announcedRef = useRef(false);

  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    usertype: '',
    verifyId: '',
  });

  const [otp, setOtp] = useState('');
  const [otpCalled, setOtpCalled] = useState(false);
  const [updateCalled, setUpdateCalled] = useState(false);
  const [confirmCodeFunction, setConfirmCodeFunction] = useState();

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  useEffect(() => {
    if (languagedata.langlist) {
      let arr = Object.keys(languagedata.langlist);
      for (let i = 0; i < arr.length; i++) {
        if (auth.profile && auth.profile.lang && auth.profile.lang.langLocale && auth.profile.lang.langLocale === languagedata.langlist[arr[i]].langLocale) {
          setLangSelection(i);
        }
      }
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist, auth.profile, setMultiLanguage, setLangSelection]);

  useEffect(() => {
    if (auth.profile && auth.profile.uid) {
      setData({
        firstName: auth.profile.firstName,
        lastName: auth.profile.lastName,
        email: auth.profile.email,
        mobile: auth.profile.mobile,
        usertype: auth.profile.usertype,
        profile_image: auth.profile.profile_image ? auth.profile.profile_image : '',
        verifyId: auth.profile.verifyId ? auth.profile.verifyId : '',
      });
      if (updateCalled) {
        setLoading(false);
        if (!announcedRef.current) showToast.success(t('profile_updated'));
        announcedRef.current = false;
        setUpdateCalled(false);
      }
    }
  }, [auth.profile, t, updateCalled]);

  // The chosen photo's preview (released when it changes).
  useEffect(() => {
    if (!selectedImage) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(selectedImage);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedImage]);

  const updateField = (name, value) => {
    setData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (formError) setFormError(null);
  };

  // The email / mobile endpoints change only the signed-in user's own login
  // (Bearer ID token) and answer { success } or { success: false, code }.
  // These turn that answer into the message shown, instead of "Profile
  // updated" whatever happened.
  const signInAgainMessage = () => t('sign_in_again', { defaultValue: 'Your session has expired. Please sign in again.' });
  const isAuthRefusal = (code) => code === 'auth_required' || code === 'auth_invalid' || code === 'session_revoked' || code === 'not_signed_in';
  const emailChangeMessage = (r) => {
    if (!r || r.success !== false) return t('profile_updated');
    if (r.code === 'email_in_use') return t('user_exists');
    if (r.code === 'invalid_email') return t('proper_email');
    if (isAuthRefusal(r.code)) return signInAgainMessage();
    return t('email_update_failed', { defaultValue: 'Could not update your email. Please try again.' });
  };
  const mobileChangeMessage = (r) => {
    const code = r && r.code;
    if (code === 'mobile_in_use') return t('user_exists');
    if (isAuthRefusal(code)) return signInAgainMessage();
    if (code === 'invalid_mobile') return t('email_or_mobile_issue');
    if (code === 'otp_expired') return t('otp_expired');
    if (code === 'otp_attempts_exceeded') return t('otp_attempts_exceeded', { defaultValue: 'Too many wrong codes. Please request a new OTP.' });
    if (code === 'otp_mismatch' || code === 'otp_not_requested') return t('otp_validate_error');
    if (code) return t('mobile_update_failed', { defaultValue: 'Could not update your mobile number. Please try again.' });
    // No code (network error): the old mapping, where 'Error updating user'
    // was the only "number already in use" signal.
    return r && r.error === 'Error updating user' ? t('user_exists') : t('otp_validate_error');
  };

  const completeSubmit = () => {
    setUpdateCalled(true);
    let arr = Object.keys(languagedata.langlist);
    localStorage.setItem('lang', JSON.stringify({ langLocale: multiLanguage[arr[langSelection]].langLocale, dateLocale: multiLanguage[arr[langSelection]].dateLocale }));

    dispatch(
      updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobile: data.mobile,
        verifyId: data.verifyId,
        lang: { langLocale: multiLanguage[arr[langSelection]].langLocale, dateLocale: multiLanguage[arr[langSelection]].dateLocale },
      })
    );

    if (selectedImage) {
      updateWebProfileImage(selectedImage);
    }

    const lang = multiLanguage[arr[langSelection]];
    // Only the default + the user's language are loaded at start: fetch the
    // chosen pack first (once), then switch — only if this is still the
    // latest save (a slow download never overrides a newer choice).
    const request = ++langRequestRef.current;
    // A pack that is not loaded yet keeps the Save button busy until the
    // language has switched ("Profile updated" then comes from the profile
    // update below); an already-loaded one switches at once, as before.
    const needsDownload = !lang.keyValuePairs;
    if (needsDownload) setLangPending(true);
    dispatch(api.loadLanguagePack(lang.langLocale))
      .then((pack) => {
        if (request !== langRequestRef.current) return null;
        setLangPending(false);
        if (!pack || !pack.success) {
          showToast.error(t('no_internet_connection'));
          return null;
        }
        i18n.addResourceBundle(lang.langLocale, 'translations', pack.keyValuePairs, true, true);
        i18n.changeLanguage(lang.langLocale);
        moment.locale(lang.dateLocale);
        return null;
      })
      .catch(() => {
        if (request === langRequestRef.current) setLangPending(false);
      });
    setSelectedImage(null);
    if (!needsDownload) {
      announcedRef.current = true;
      showToast.success(t('profile_updated'));
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError(null);
    setErrors({});
    if (data.email !== auth.profile.email && data.mobile !== auth.profile.mobile) {
      setFormError(t('update_any'));
      return;
    }
    if (!(data.firstName && data.firstName.length > 0 && data.lastName && data.lastName.length > 0)) {
      const required = L('form_required', 'This field is required');
      setErrors({
        firstName: data.firstName && data.firstName.length > 0 ? null : required,
        lastName: data.lastName && data.lastName.length > 0 ? null : required,
      });
      return;
    }
    if (data.email !== auth.profile.email) {
      if (EMAIL_RE.test(data.email)) {
        setLoading(true);
        checkUserExists({ email: data.email }).then(async (res) => {
          const decryptedRes = await processApiResponse(res);
          if (decryptedRes.users && decryptedRes.users.length > 0) {
            setLoading(false);
            showToast.error(t('user_exists'));
          } else if (decryptedRes.error) {
            setLoading(false);
            showToast.error(t('email_or_mobile_issue'));
          } else {
            const r = await dispatch(updateProfileWithEmail({ email: data.email, firstName: data.firstName, lastName: data.lastName }));
            setLoading(false);
            if (!r || r.success !== false) showToast.success(emailChangeMessage(r));
            else setFormError(emailChangeMessage(r));
          }
        });
      } else {
        setErrors({ email: t('proper_email') });
      }
    } else if (data.mobile !== auth.profile.mobile) {
      if (data.mobile && data.mobile.length > 6) {
        setLoading(true);
        checkUserExists({ mobile: data.mobile }).then(async (res) => {
          const decryptedRes = await processApiResponse(res);
          if (decryptedRes.users && decryptedRes.users.length > 0) {
            setLoading(false);
            showToast.error(t('user_exists'));
          } else if (decryptedRes.error) {
            setLoading(false);
            showToast.error(t('email_or_mobile_issue'));
          } else {
            setLoading(false);
            setOtpCalled(true);

            if (settings.customMobileOTP) {
              dispatch(requestMobileOtp(data.mobile));
            } else {
              window.recaptchaVerifier = await new RecaptchaVerifier(authRef(), 'sign-in-button', {
                size: 'invisible',
              });

              if (auth.profile.mobile && auth.profile.mobile.length > 6) {
                await unlink(authRef().currentUser, 'phone');
              }

              const linkObj = await linkWithPhoneNumber(authRef().currentUser, data.mobile, window.recaptchaVerifier);
              setConfirmCodeFunction(linkObj);
            }
          }
        });
      } else {
        setErrors({ mobile: t('mobile_no_blank_error') });
      }
    } else {
      setLoading(true);
      completeSubmit();
    }
  };

  const handleVerify = async () => {
    if (otp && otp.length === 6 && !isNaN(otp)) {
      setLoading(true);
      if (settings.customMobileOTP) {
        const res = await updateAuthMobile(data.mobile, otp);
        if (res && res.success) {
          completeSubmit();
        } else {
          setOtp('');
          setLoading(false);
          setFormError(mobileChangeMessage(res));
        }
      } else {
        if (confirmCodeFunction) {
          confirmCodeFunction
            .confirm(otp)
            .then(() => {
              completeSubmit();
            })
            .catch(() => {
              setOtp('');
              setLoading(false);
              window.recaptchaVerifier.clear();
              setFormError(t('otp_validate_error'));
            });
        }
      }
    } else {
      setOtp('');
      setFormError(t('otp_validate_error'));
    }
    setOtpCalled(false);
    window.recaptchaVerifier = null;
  };

  const handleClose = () => {
    setOtp('');
    setOtpCalled(false);
    window.recaptchaVerifier = null;
  };

  const profileImageChange = async (e) => {
    setSelectedImage(e.target.files[0]);
  };

  if (auth.loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={420} />
      </Box>
    );
  }

  const nameLocked = auth.profile?.source === 'oromiabank';
  // The old condition (usertype !== 'driver' || !== 'customer' || !== 'admin')
  // is true for every user: email and phone have always been read-only here.
  const contactLocked = true;
  const showVerifyId = ((settings && settings.imageIdApproval) || (data && data.verifyId)) && auth.profile.usertype !== 'admin';
  const langList = Array.isArray(multiLanguage) ? multiLanguage : multiLanguage ? Object.values(multiLanguage) : [];
  const photo = previewUrl || data.profile_image || null;
  const busy = loading || langPending;
  const describedBy = (name) => (errors[name] ? `profile-${name}-error` : undefined);
  // An Ethiopian number shows as its 9 digits after +251; any other as stored.
  const ethiopian = !data.mobile || /^\+251\d{0,9}$/.test(String(data.mobile));
  const phoneValue = ethiopian ? toNineDigits(data.mobile) : String(data.mobile);

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div id="sign-in-button" />
      <SectionCard icon={<PersonOutlineRoundedIcon />} hue="info" title={calmCaps(t('profile_title'))} id="profile">
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {/* Photo: tap to choose a new one; it is saved with the profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <ButtonBase
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              aria-label={t('upload_profile_image')}
              sx={{ borderRadius: '50%', position: 'relative', flex: 'none', '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 } }}
            >
              <Avatar
                src={photo || require('../assets/img/profilePic.png')}
                alt={t('profile_image')}
                sx={{ width: 96, height: 96, bgcolor: c.SURFACE_ALT }}
              />
              <Box
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: c.WHITE,
                  border: `1px solid ${c.OUTLINE_STRONG}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: c.INK_2,
                }}
              >
                <PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
            </ButtonBase>
            <input onChange={(event) => profileImageChange(event)} multiple={false} ref={fileInputRef} type="file" accept="image/*" hidden />
            <Typography component="span" aria-hidden="true" sx={{ fontSize: 14.5, fontWeight: 600, color: c.INK }}>
              {t('profile_image')}
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, columnGap: 2, rowGap: 2.25 }}>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="firstName" disabled={nameLocked}>
                {t('firstname')}
              </FieldLabel>
              <TextField
                id="firstName"
                name="firstName"
                fullWidth
                required
                autoComplete="given-name"
                disabled={nameLocked}
                value={data.firstName || ''}
                error={!!errors.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                inputProps={{ 'aria-describedby': describedBy('firstName') }}
              />
              <FieldError id="profile-firstName-error">{errors.firstName}</FieldError>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="lastName" disabled={nameLocked}>
                {t('lastname')}
              </FieldLabel>
              <TextField
                id="lastName"
                name="lastName"
                fullWidth
                required
                autoComplete="family-name"
                disabled={nameLocked}
                value={data.lastName || ''}
                error={!!errors.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                inputProps={{ 'aria-describedby': describedBy('lastName') }}
              />
              <FieldError id="profile-lastName-error">{errors.lastName}</FieldError>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="email" disabled={contactLocked}>
                {t('email')}
              </FieldLabel>
              <TextField
                id="email"
                name="email"
                type="email"
                fullWidth
                autoComplete="email"
                disabled={contactLocked}
                value={data.email || ''}
                error={!!errors.email}
                onChange={(e) => updateField('email', e.target.value)}
                inputProps={{ inputMode: 'email', 'aria-describedby': describedBy('email') }}
              />
              <FieldError id="profile-email-error">{errors.email}</FieldError>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="mobile" disabled={contactLocked}>
                {t('phone')}
              </FieldLabel>
              <TextField
                id="mobile"
                name="mobile"
                type="tel"
                fullWidth
                dir="ltr"
                disabled={contactLocked}
                value={phoneValue}
                error={!!errors.mobile}
                onChange={(e) => updateField('mobile', toEthiopianE164(e.target.value) || toNineDigits(e.target.value))}
                inputProps={{ inputMode: 'numeric', maxLength: ethiopian ? 9 : undefined, autoComplete: 'off', 'aria-describedby': describedBy('mobile') }}
                InputProps={
                  ethiopian
                    ? {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box component="span" sx={{ color: c.INK_2, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              +251
                            </Box>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
              <FieldError id="profile-mobile-error">{errors.mobile}</FieldError>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="usertype" disabled>
                {t('usertype')}
              </FieldLabel>
              <TextField id="usertype" name="usertype" fullWidth disabled value={data.usertype ? t(data.usertype, { defaultValue: data.usertype }) : ''} />
            </Box>
            {showVerifyId ? (
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel htmlFor="verifyId">{t('verify_id')}</FieldLabel>
                <TextField id="verifyId" name="verifyId" fullWidth value={data.verifyId || ''} onChange={(e) => updateField('verifyId', e.target.value)} />
              </Box>
            ) : null}
            {langList.length > 1 ? (
              <Box sx={{ minWidth: 0 }}>
                <FieldLabel id="language-select-label">{t('lang')}</FieldLabel>
                <TextField select fullWidth value={langSelection} onChange={(event) => setLangSelection(event.target.value)} SelectProps={{ labelId: 'language-select-label', id: 'language-select' }}>
                  {langList.map((lang, index) => (
                    <MenuItem key={lang.langLocale || index} value={index}>
                      {lang.langName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            ) : null}
          </Box>

          {formError ? (
            <InlineMessage kind="error" sx={{ mt: 2 }}>
              {formError}
            </InlineMessage>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={busy}
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <CheckRoundedIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {busy ? L('form_saving', 'Saving…') : calmCaps(t('submit'))}
            </Button>
          </Box>
        </Box>
      </SectionCard>

      {/* The code sent to the new number */}
      <Dialog open={otpCalled} onClose={handleClose} maxWidth="xs" fullWidth fullScreen={phoneScreen} aria-labelledby="profile-otp-title">
        <DialogTitle id="profile-otp-title" sx={{ pr: 8 }}>
          {t('otp_here')}
          <IconButton aria-label={L('close_dialog', 'Close')} onClick={handleClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <FieldLabel htmlFor="profile-otp">{String(t('otp')).replace(/\s*:\s*$/, '')}</FieldLabel>
          <TextField
            id="profile-otp"
            autoFocus
            fullWidth
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
            sx={{ '& input': { fontVariantNumeric: 'tabular-nums', letterSpacing: '0.3em', fontSize: 20 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={handleClose} disabled={loading}>
            {L('confirm_cancel', 'Cancel')}
          </Button>
          <Button variant="contained" onClick={handleVerify} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>
            {t('verify_otp')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyProfile;
